import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, X, Send, RotateCcw, Loader2, Mic, Square, Check, Ban, Volume2, VolumeX, Ear, EarOff } from "lucide-react";
import { useSpeech } from "@/hooks/useSpeech";
import { useWakeWord, wakeEngine } from "@/hooks/useWakeWord";
import { toast } from "sonner";

const SUGGESTIONS = [
    "Bu ay ne kadar tahsilat yaptık?",
    "Borcu olan hastalar kimler?",
    "Bu haftaki randevularım neler?",
    "Ödeme hatırlatması için nazik bir WhatsApp mesajı yaz",
    "Bugün 500 TL malzeme gideri ekle",
];
const SID_KEY = "dd_ai_session";
const WAKE_LABEL = {
    loading: "Ses modeli yükleniyor…",
    listening: "Dinliyor • \"Hey DentDefter\"",
    paused: "Eller serbest • beklemede",
    error: "Mikrofon/ses tanıma hatası",
};
const newSid = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const stripAction = (t) => (t || "").split("```action")[0].trim();

const ACTION_STATUS = {
    done: "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed: "text-rose-700 bg-rose-50 border-rose-200",
    cancelled: "text-slate-500 bg-slate-50 border-slate-200",
};

const ActionCard = ({ action, onUpdate }) => {
    const [busy, setBusy] = useState(false);
    async function act(kind) {
        setBusy(true);
        try {
            const { data } = await api.post(`/ai/actions/${action.id}/${kind}`);
            onUpdate(data);
            if (data.status === "done") toast.success(data.result);
            else if (data.status === "failed") toast.error(data.result);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    return (
        <div className="mt-2 rounded-xl border border-[#00A896]/40 bg-teal-50/60 p-3" data-testid={`ai-action-card-${action.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-[#0F4C5C] font-semibold mb-1">Onay bekleyen işlem</div>
            <div className="text-sm font-medium text-slate-900" data-testid="ai-action-summary">{action.summary}</div>
            {action.status === "pending" ? (
                <div className="flex gap-2 mt-2.5">
                    <Button size="sm" className="dk-btn-primary h-8" disabled={busy} onClick={() => act("confirm")} data-testid="ai-action-confirm-btn">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Onayla
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => act("cancel")} data-testid="ai-action-cancel-btn">
                        <Ban className="w-3.5 h-3.5 mr-1" />Vazgeç
                    </Button>
                </div>
            ) : (
                <div className={`mt-2 text-xs px-2 py-1 rounded-lg border inline-block ${ACTION_STATUS[action.status] || ""}`} data-testid="ai-action-result">
                    {action.status === "running" ? "İşleniyor…" : action.result}
                </div>
            )}
        </div>
    );
};

const SpeakBtn = ({ idx, content, speech }) => {
    if (!speech.supported || !content) return null;
    const active = speech.speakingId === idx;
    return (
        <button
            type="button"
            onClick={() => (active ? speech.stop() : speech.speak(idx, content))}
            className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 transition-colors ${active ? "bg-[#00A896] text-white" : "text-slate-500 hover:text-[#0F4C5C] hover:bg-slate-100"}`}
            data-testid={`ai-speak-btn-${idx}`}
            title={active ? "Okumayı durdur" : "Sesli oku"}
        >
            {active ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
            {active ? "Durdur" : "Dinle"}
        </button>
    );
};

const Bubble = ({ m, idx, onAction, speech }) => (
    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${idx}`}>
        <div
            className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                    ? "bg-[#0F4C5C] text-white rounded-br-md"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
            }`}
        >
            {m.role === "user" ? (
                m.content
            ) : (
                <>
                    <div className="dd-md">
                        <ReactMarkdown>{stripAction(m.content) || "…"}</ReactMarkdown>
                    </div>
                    {m.action && <ActionCard action={m.action} onUpdate={(a) => onAction(idx, a)} />}
                    <SpeakBtn idx={idx} content={m.content} speech={speech} />
                </>
            )}
        </div>
    </div>
);

async function streamChat(sessionId, message, onDelta) {
    const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message }),
    });
    if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Asistan yanıt veremedi");
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
            const data = ev.replace(/^data: /, "").trim();
            if (!data || data === "[DONE]") continue;
            const j = JSON.parse(data);
            if (j.error) throw new Error(j.error);
            if (j.delta) onDelta(j.delta);
            if (j.action) onDelta("", j.action);
        }
    }
}

export const AiAssistant = () => {
    const [open, setOpen] = useState(false);
    const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || newSid());
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);
    const recRef = useRef(null);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const speech = useSpeech();
    const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("dd_ai_voice") === "1");
    const replyIdxRef = useRef(-1);

    function toggleVoice() {
        const next = !voiceOn;
        setVoiceOn(next);
        localStorage.setItem("dd_ai_voice", next ? "1" : "0");
        if (!next) speech.stop();
        else if (!speech.hasTrVoice)
            toast.message("Bilgisayarınızda Türkçe ses bulunamadı", {
                description: "Windows Ayarlar → Saat ve dil → Konuşma bölümünden Türkçe ses ekleyebilirsiniz.",
            });
    }

    const updateAction = (idx, action) =>
        setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, action } : x)));

    function beep() {
        try {
            const ctx = new AudioContext();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.frequency.value = 880;
            g.gain.setValueAtTime(0.15, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
            o.connect(g).connect(ctx.destination);
            o.start();
            o.stop(ctx.currentTime + 0.25);
            setTimeout(() => ctx.close(), 400);
        } catch {
            /* no audio */
        }
    }

    // Auto mode: stops after ~1.4s silence once speech is heard (hands-free).
    function watchSilence(stream, rec) {
        const ctx = new AudioContext();
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(an);
        const buf = new Float32Array(an.fftSize);
        const t0 = Date.now();
        let heard = false;
        let lastLoud = Date.now();
        const iv = setInterval(() => {
            an.getFloatTimeDomainData(buf);
            const rms = Math.sqrt(buf.reduce((a, v) => a + v * v, 0) / buf.length);
            if (rms > 0.02) {
                heard = true;
                lastLoud = Date.now();
            }
            const silentFor = Date.now() - lastLoud;
            if ((heard && silentFor > 1400) || (!heard && Date.now() - t0 > 7000) || Date.now() - t0 > 20000) {
                if (!heard) rec._discard = true;
                if (rec.state === "recording") rec.stop();
            }
        }, 100);
        return () => {
            clearInterval(iv);
            ctx.close();
        };
    }

    async function startRecording(auto = false) {
        speech.stop();
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            toast.error("Mikrofona erişilemedi. Tarayıcı izinlerini kontrol edin.");
            return;
        }
        const chunks = [];
        const rec = new MediaRecorder(stream);
        const unwatch = auto ? watchSilence(stream, rec) : null;
        rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        rec.onstop = async () => {
            unwatch?.();
            stream.getTracks().forEach((t) => t.stop());
            clearTimeout(rec._limit);
            setRecording(false);
            if (rec._discard) return;
            const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
            if (blob.size < 1500) return;
            setTranscribing(true);
            try {
                const fd = new FormData();
                const ext = (rec.mimeType || "").includes("mp4") ? "mp4" : "webm";
                fd.append("file", blob, `kayit.${ext}`);
                const { data } = await api.post("/ai/transcribe", fd);
                if (data.text) send(data.text, true);
                else toast.error("Ses anlaşılamadı, tekrar deneyin");
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setTranscribing(false);
            }
        };
        rec.start();
        rec._limit = setTimeout(() => rec.state === "recording" && rec.stop(), 60000);
        recRef.current = rec;
        setRecording(true);
    }

    function toggleMic() {
        if (recording) recRef.current?.stop();
        else startRecording(false);
    }

    const [handsFree, setHandsFree] = useState(() => localStorage.getItem("dd_ai_handsfree") === "1");
    const wakeState = useWakeWord({
        enabled: handsFree,
        paused: busy || recording || transcribing || speech.speakingId !== null,
        onWake: () => {
            setOpen(true);
            beep();
            startRecording(true);
        },
    });

    function toggleHandsFree() {
        const next = !handsFree;
        setHandsFree(next);
        localStorage.setItem("dd_ai_handsfree", next ? "1" : "0");
        if (next) toast.message('"Hey DentDefter" deyin, sonra sorunuzu söyleyin', {
            description: wakeEngine() === "offline" ? "İlk kullanımda ~37 MB Türkçe ses modeli bir kez indirilir; ses bilgisayardan çıkmaz." : "Chrome/Edge ses tanıması kullanılıyor.",
        });
    }

    const sendingRef = useRef(false);

    useEffect(() => {
        let stale = false;
        localStorage.setItem(SID_KEY, sid);
        api.get("/ai/history", { params: { session_id: sid } })
            .then((r) => !stale && !sendingRef.current && setMsgs(r.data))
            .catch(() => {});
        return () => {
            stale = true;
        };
    }, [sid]);

    function newChat() {
        if (busy) return;
        speech.stop();
        setMsgs([]);
        setSid(newSid());
    }

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [msgs, open]);

    async function send(text, fromVoice = false) {
        const q = (text ?? input).trim();
        if (!q || sendingRef.current) return;
        if (recRef.current?.state === "recording") {
            recRef.current._discard = true;
            recRef.current.stop();
        }
        setInput("");
        setBusy(true);
        sendingRef.current = true;
        speech.stop();
        let full = "";
        setMsgs((m) => {
            replyIdxRef.current = m.length + 1;
            return [...m, { role: "user", content: q }, { role: "assistant", content: "" }];
        });
        const append = (d, action) => {
            full += d;
            setMsgs((m) => {
                const c = [...m];
                const last = c[c.length - 1];
                if (!last || last.role !== "assistant") return [...c, { role: "assistant", content: d, action }];
                c[c.length - 1] = { ...last, content: last.content + d, action: action || last.action };
                return c;
            });
        };
        try {
            await streamChat(sid, q, append);
            if (voiceOn || fromVoice) speech.speak(replyIdxRef.current, full);
        } catch (e) {
            append(`\n\n_${e.message}_`);
        } finally {
            setBusy(false);
            sendingRef.current = false;
        }
    }

    return (
        <>
            {open && (
                <div
                    className="fixed z-50 bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(600px,calc(100vh-8rem))] flex flex-col rounded-3xl border border-slate-200 bg-slate-50/95 backdrop-blur-xl shadow-2xl overflow-hidden dd-ai-pop no-print"
                    data-testid="ai-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 bg-[#0A1218] text-white">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#00A896] to-[#0F4C5C] flex items-center justify-center">
                                <Sparkles className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold">DentDefter Asistan</div>
                                <div className="text-[10px] text-teal-300 uppercase tracking-wider" data-testid="ai-status-line">
                                    {handsFree ? WAKE_LABEL[wakeState] || "Eller serbest" : "Ücretsiz • Yapay Zeka"}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={toggleHandsFree} data-testid="ai-handsfree-toggle"
                                title={handsFree ? "Eller serbest açık — \"Hey DentDefter\" deyin" : "Eller serbest (Hey DentDefter)"}
                                className={`p-2 rounded-lg transition-colors ${handsFree ? "bg-[#F4A261] text-[#0A1218]" : "hover:bg-white/10 text-slate-300"}`}>
                                {handsFree ? <Ear className="w-4 h-4" /> : <EarOff className="w-4 h-4" />}
                            </button>
                            {speech.supported && (
                                <button onClick={toggleVoice} title={voiceOn ? "Sesli yanıt açık" : "Sesli yanıt kapalı"} data-testid="ai-voice-toggle"
                                    className={`p-2 rounded-lg transition-colors ${voiceOn ? "bg-[#00A896] text-white" : "hover:bg-white/10 text-slate-300"}`}>
                                    {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                                </button>
                            )}
                            <button onClick={newChat} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Yeni sohbet" data-testid="ai-new-chat-btn">
                                <RotateCcw className="w-4 h-4" />
                            </button>
                            <button onClick={() => { speech.stop(); setOpen(false); }} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Kapat" data-testid="ai-close-btn">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
                        {msgs.length === 0 && (
                            <div className="text-sm text-slate-600">
                                <p className="mb-3">Merhaba! Kliniğinizin gelirleri, borçlu hastaları, randevuları hakkında soru sorabilir ya da mesaj metni yazdırabilirsiniz.</p>
                                <div className="flex flex-col gap-2">
                                    {SUGGESTIONS.map((s, i) => (
                                        <button key={s} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`}
                                            className="text-left text-xs px-3 py-2 rounded-xl border border-slate-200 bg-white hover:border-[#00A896] hover:text-[#0F4C5C] transition-colors">
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {msgs.map((m, i) => <Bubble key={i} m={m} idx={i} onAction={updateAction} speech={speech} />)}
                        {busy && msgs[msgs.length - 1]?.content === "" && (
                            <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Düşünüyor…</div>
                        )}
                        <div ref={endRef} />
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 bg-white flex gap-2">
                        <button type="button" onClick={toggleMic} disabled={busy || transcribing}
                            title={recording ? "Kaydı bitir" : "Sesli sor"} data-testid="ai-mic-btn"
                            className={`w-10 flex-shrink-0 rounded-xl border flex items-center justify-center transition-colors ${recording ? "bg-rose-500 border-rose-500 text-white animate-pulse" : "border-slate-200 text-slate-600 hover:border-[#00A896] hover:text-[#0F4C5C]"}`}>
                            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-4 h-4" />}
                        </button>
                        <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000} placeholder={recording ? "Dinliyorum… bitince ■ basın" : transcribing ? "Yazıya çevriliyor…" : "Bir şey sorun ya da işlem isteyin…"}
                            className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:border-[#00A896]" data-testid="ai-input" />
                        <Button type="submit" disabled={busy || !input.trim()} className="dk-btn-primary rounded-xl px-3" data-testid="ai-send-btn">
                            <Send className="w-4 h-4" />
                        </Button>
                    </form>
                </div>
            )}
            {handsFree && wakeState === "listening" && !open && (
                <span className="fixed z-50 bottom-[4.6rem] right-5 sm:right-7 w-3 h-3 rounded-full bg-[#F4A261] ring-2 ring-white animate-pulse no-print" data-testid="ai-wake-indicator" title='"Hey DentDefter" dinleniyor' />
            )}
            <button
                onClick={() => {
                    if (open) speech.stop();
                    setOpen((o) => !o);
                }}
                className="fixed z-50 bottom-6 right-4 sm:right-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#00A896] to-[#0F4C5C] text-white shadow-xl shadow-teal-900/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform no-print"
                title="Yapay Zeka Asistanı"
                data-testid="ai-fab"
            >
                {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
            </button>
        </>
    );
};
